import type { FrameEvidenceAttempt } from './types';
import { frameEvidence, programFrameSourceId } from './frame-evidence';
import { videoSamples } from './sample-evidence';
import { createBoundaryState, decideBoundary, nextDefaultSeqBase, noteSeqEmitted, requestBoundary } from './boundary';
import { createDriftCorrector, stretchAudioData } from './audio-drift';
import { avcCodecStringFromAvcC, h264LevelHex, selectAudioCodec, selectVideoCodec, type HardwareAcceleration, type SelectedAudio } from './codec-select';
import {
  createFmp4Splitter,
  isSyncSampleFlags,
  parseFragment,
  parseInit,
  shiftFragmentDecodeTime,
  type Fmp4Splitter,
  type InitTrackInfo,
} from './fmp4-split';
import { Mp4MuxerAdapter, type MuxerAdapter } from './muxer-adapter';
import { PartsMuxerAdapter } from './parts-muxer';
import { av1SequenceConfig, installAv1Config, type Av1SequenceConfig } from './av1-config';
import type { EncodedPacketSink, EncodingUpdate, Segmenter, SegmenterOptions, SegmenterState } from './types';

/** Fragments shorter than this fraction of the segment length are never cut by the muxer. */
const MIN_FRAGMENT_FRACTION = 0.9;
/**
 * Encoder watchdog: frames waiting with no output for this long mean the encoder is stuck.
 * Observed with Chrome's hardware H.264 encoder (VideoToolbox) in headless automation: it accepts
 * frames and emits nothing until flush(). A stuck encoder would freeze the live stream.
 */
const STALL_MS = 1500;
/** A queue above this with no output is a stalled encoder, regardless of the drop budget. */
const STALL_QUEUE = 2;

type Queued =
  | { kind: 'video'; chunk: EncodedVideoChunk; meta?: EncodedVideoChunkMetadata }
  | { kind: 'audio'; chunk: EncodedAudioChunk; meta?: EncodedAudioChunkMetadata };

/**
 * Maps a track's own timestamps onto one shared clock (µs since `start()`), anchored by the
 * wall-clock time at which the track's first frame was read. Audio and video processors do not
 * promise a common time base, and a replaced track restarts its own clock.
 */
function createRebaser(clockOriginMs: number): (timestampUs: number) => number {
  let offsetUs: number | null = null;
  return (timestampUs) => {
    if (offsetUs === null) offsetUs = (performance.now() - clockOriginMs) * 1000 - timestampUs;
    return Math.round(timestampUs + offsetUs);
  };
}

function copyAudioData(data: AudioData, timestamp: number): AudioData {
  const format = data.format!;
  const planar = format.endsWith('-planar');
  const planes = planar ? data.numberOfChannels : 1;
  const sizes: number[] = [];
  for (let i = 0; i < planes; i++) sizes.push(data.allocationSize({ planeIndex: i }));
  const buffer = new Uint8Array(sizes.reduce((a, b) => a + b, 0));
  let offset = 0;
  for (let i = 0; i < planes; i++) {
    data.copyTo(buffer.subarray(offset, offset + sizes[i]!), { planeIndex: i });
    offset += sizes[i]!;
  }
  return new AudioData({
    format,
    sampleRate: data.sampleRate,
    numberOfFrames: data.numberOfFrames,
    numberOfChannels: data.numberOfChannels,
    timestamp,
    data: buffer,
  });
}

function toError(e: unknown): Error {
  return e instanceof Error ? e : new Error(String(e));
}

function copyDescription(d: AllowSharedBufferSource): Uint8Array {
  return ArrayBuffer.isView(d) ? new Uint8Array(d.buffer, d.byteOffset, d.byteLength).slice() : new Uint8Array(d).slice();
}

function sameBytes(a: Uint8Array, b: Uint8Array): boolean {
  if (a.length !== b.length) return false;
  for (let i = 0; i < a.length; i++) if (a[i] !== b[i]) return false;
  return true;
}

/** Everything that depends on one video encoder: its config, init, muxer, splitter and sequence counter. */
interface Lane {
  id: string;
  main: boolean;
  codec: 'h264' | 'av1';
  fps: number;
  videoKbps: number;
  hardwareAcceleration?: HardwareAcceleration;
  presetConfig?: VideoEncoderConfig;
  status: 'running' | 'failed' | 'dropped';
  encoder: VideoEncoder | null;
  config: VideoEncoderConfig | null;
  /** performance.now() of the last video output (or of the encoder's creation). */
  lastOutputAt: number;
  /** Set when the watchdog replaced a stuck encoder with a software one (main only). */
  encoderFallback: string | null;
  /** Size the encoder is configured for; changes only through updateEncoding({ width, height }). */
  encWidth: number;
  encHeight: number;
  /**
   * Picture size of the current init, from the encoder's decoderConfig (codedWidth/Height) when it
   * reports one, else the configured size. An encoder may encode at another size than configured
   * (Chromium re-initialises at the source size on some paths); the init must describe the stream.
   */
  initWidth: number;
  initHeight: number;
  /**
   * The newest video chunk, held back until the next one arrives so that its duration is exact (the
   * muxer needs it for the last sample of a fragment, and encoders report duration 0 or null).
   */
  heldVideo: { chunk: EncodedVideoChunk; meta?: EncodedVideoChunkMetadata } | null;
  /** Rebased timestamp of the first encoded video chunk: time zero of the MP4 timeline. */
  videoZeroUs: number | null;
  /** Rebased timestamps of forced keyframes → wall-clock capture time (ms). */
  segmentStarts: Map<number, number>;
  encoderGeneration: number;
  submittedFrames: Map<number, { track: MediaStreamTrack; timestampUs: number; encoderGeneration: number; attempt?: FrameEvidenceAttempt & { startedAt: number } }>;
  encodedFrames: Map<number, { track: MediaStreamTrack; timestampUs: number; bytes: Uint8Array; attempt?: FrameEvidenceAttempt & { startedAt: number } }>;
  avcC: Uint8Array | null;
  av1Config: Av1SequenceConfig | null;
  muxer: MuxerAdapter | null;
  muxerConfigured: boolean;
  /**
   * MP4 time (µs after videoZeroUs) at which the current muxer starts. mp4-muxer always starts its
   * timeline at 0, so a muxer that takes over after an encoder reconfigure is fed timestamps minus
   * this offset and its fragments' decode times are shifted back by it.
   */
  muxerOffsetUs: number;
  queue: Queued[];
  // Independent encoders deliver callbacks at different wall-clock times. Mux in
  // timestamp order so a video configuration change cannot retire a muxer before
  // the AAC packets belonging to its last pictures have arrived.
  encodedVideo: Extract<Queued, { kind: 'video' }>[];
  encodedAudio: Extract<Queued, { kind: 'audio' }>[];
  encodedBytes: number;
  initTracks: InitTrackInfo[];
  videoTrackId: number;
  seq: number;
  gopStartSeq: number;
  droppedFrames: number;
  /** Extra lanes: updateLaneEncoding, applied at the next boundary. Main uses the shared pendingEncoding. */
  pendingEncoding: EncodingUpdate | null;
  /** Extra lanes below the main fps: µs between kept frames, and when the next one is due. 0 keeps every frame. */
  frameIntervalUs: number;
  nextDueUs: number | null;
}

function newLane(id: string, main: boolean, o: {
  codec?: 'h264' | 'av1'; width: number; height: number; fps: number; videoKbps: number;
  videoConfig?: VideoEncoderConfig; hardwareAcceleration?: HardwareAcceleration;
}, mainFps: number): Lane {
  return {
    id, main, codec: o.codec ?? 'h264', fps: o.fps, videoKbps: o.videoKbps, hardwareAcceleration: o.hardwareAcceleration,
    presetConfig: o.videoConfig, status: 'running', encoder: null, config: null, lastOutputAt: 0, encoderFallback: null,
    encWidth: o.width, encHeight: o.height, initWidth: o.width, initHeight: o.height, heldVideo: null, videoZeroUs: null,
    encoderGeneration: 0, segmentStarts: new Map(), submittedFrames: new Map(), encodedFrames: new Map(), avcC: null, av1Config: null, muxer: null, muxerConfigured: false, muxerOffsetUs: 0, queue: [],
    encodedVideo: [], encodedAudio: [], encodedBytes: 0, initTracks: [], videoTrackId: -1, seq: 0, gopStartSeq: 0,
    droppedFrames: 0, pendingEncoding: null,
    frameIntervalUs: !main && o.fps < mainFps ? 1e6 / o.fps : 0, nextDueUs: null,
  };
}

function validEncodingUpdate(p: EncodingUpdate): void {
  for (const k of ['width', 'height'] as const) {
    const v = p[k];
    if (v !== undefined && !(Number.isInteger(v) && v > 0 && v % 2 === 0)) {
      throw new Error(`updateEncoding: ${k} must be a positive even integer, got ${v}`);
    }
  }
}

/**
 * Encoder queue depth above which non-boundary frames are dropped. Allows a short encoder scheduling burst: at 60 fps
 * this is at most about 100 ms of queued input, while a sustained overload still sheds non-boundary frames.
 */
export function maxVideoQueueFor(fps: number): number {
  return Math.max(4, Math.min(6, Math.ceil(fps / 10)));
}

/** How long stop() waits for an extra lane's encoder flush after main is finalised; a slower lane loses its last part. */
const EXTRA_FLUSH_MS = 500;

export function createSegmenter(
  stream: MediaStream,
  sink: EncodedPacketSink,
  opts: SegmenterOptions,
  /** Test seam; defaults to mp4-muxer. */
  muxerFactory: () => MuxerAdapter = () => opts.segmentMs === 200 ? new PartsMuxerAdapter() : new Mp4MuxerAdapter(),
): Segmenter & { waitForSourceCleanup(): Promise<void> } {
  if (opts.codec && opts.codec !== 'h264' && opts.codec !== 'av1') throw new Error(`Unsupported codec '${opts.codec}'`);
  if (opts.seqBase !== undefined && !(Number.isSafeInteger(opts.seqBase) && opts.seqBase >= 0)) {
    throw new Error(`seqBase must be a non-negative safe integer, got ${opts.seqBase}`);
  }
  const extraOptions = opts.lanes ?? [];
  for (const l of extraOptions) {
    if (l.id === 'main' || extraOptions.filter((o) => o.id === l.id).length > 1) throw new Error(`segmenter: lane id '${l.id}' is reserved or repeated`);
    if (l.codec !== 'h264' && l.codec !== 'av1') throw new Error(`Unsupported codec '${l.codec}'`);
    if (!(l.fps > 0 && l.fps <= opts.fps)) throw new Error(`segmenter: lane '${l.id}' fps must be positive and at most ${opts.fps}`);
  }
  const maxVideoQueue = maxVideoQueueFor(opts.fps);

  const mainLane = newLane('main', true, opts, opts.fps);
  const lanes: Lane[] = [mainLane, ...extraOptions.map((l) => newLane(l.id, false, l, opts.fps))];
  /** Lane ids reach the sink only when there is more than one lane: a single-lane sink sees exactly what it did before. */
  const multiLane = lanes.length > 1;
  /** Part start (ms on the shared clock) → seq and lane that emitted it first: lanes must agree on every shared start. */
  const partSeqs = new Map<number, { seq: number; lane: Lane }>();

  // Shared across lanes/restarts until hashes settle, so slow WebCrypto cannot grow copies unbounded.
  let pendingEvidenceHashBytes = 0;
  function originalSampleDigest(bytes: Uint8Array, attempt: FrameEvidenceAttempt): Promise<string | null> {
    const size = bytes.length;
    if (pendingEvidenceHashBytes + size > 8 * 1024 * 1024) {
      attempt.end();
      return Promise.resolve(null);
    }
    pendingEvidenceHashBytes += size;
    try {
      // Private snapshot made synchronously before the sink can see or mutate segment.bytes.
      return crypto.subtle.digest('SHA-256', bytes.slice().buffer).then(
        hash => attempt.isCurrent() ? Array.from(new Uint8Array(hash), b => b.toString(16).padStart(2, '0')).join('') : null,
        () => { attempt.end(); return null; },
      ).finally(() => { pendingEvidenceHashBytes -= size; });
    } catch {
      pendingEvidenceHashBytes -= size;
      attempt.end();
      return Promise.resolve(null);
    }
  }

  let source = stream;
  let pendingSource: MediaStream | null = null;
  let pendingEncoding: EncodingUpdate | null = null;
  let audioReconfigure = false;

  let running = false;
  /** start()/stop() run one at a time, in call order (see the public API below). */
  let lifecycle: Promise<void> = Promise.resolve();
  // Latest requested kind is insufficient: stop -> start can still have a shutdown queued.
  let pendingLifecycleOperations = 0;
  let lastOp: { kind: 'start' | 'stop'; done: Promise<void> } | null = null;
  let generation = 0;
  let failed = false;

  let audioEncoder: AudioEncoder | null = null;
  let audioSel: SelectedAudio | null = null;
  let audioConfiguring: Promise<void> | null = null;
  let expectAudio = false;

  let clockOriginMs = 0;
  const gopMs = opts.segmentMs === 200 ? 1000 : opts.segmentMs;
  let boundary = createBoundaryState(gopMs);
  let lastVideoTs = -Infinity;
  let lastAudioTs = -Infinity;

  const readers = new Set<ReadableStreamDefaultReader<VideoFrame> | ReadableStreamDefaultReader<AudioData>>();
  const sourceWork = new Set<Promise<void>>();
  let sourceCleanupFailure: Error | null = null;
  function ownSourceWork(work: Promise<void>, reportFailure: boolean): void {
    const settled = work.catch(error => {
      sourceCleanupFailure ??= toError(error);
      if (reportFailure) fail(error);
    }).catch(error => { sourceCleanupFailure ??= toError(error); });
    sourceWork.add(settled);
    void settled.then(() => sourceWork.delete(settled));
  }
  /** Explicit opt-in settlement after stop; legacy stop keeps its existing nonblocking reader behavior. */
  async function waitForSourceCleanup(): Promise<void> {
    while (sourceWork.size) await Promise.all([...sourceWork]);
    if (sourceCleanupFailure) throw sourceCleanupFailure;
  }

  let runtimeEvidence = false;
  let activeAttempt: (FrameEvidenceAttempt & { startedAt: number }) | undefined;

  function endEvidence(): void {
    activeAttempt = undefined;
    // Legacy constructor evidence is unrelated to runtime attempts and carries no attempt handle.
    if (runtimeEvidence) for (const lane of lanes) {
      lane.submittedFrames.clear(); lane.encodedFrames.clear();
    }
  }

  function beginFrameEvidence(input: { id: string; sourceId: string; expiresAtMonotonicMs: number }): FrameEvidenceAttempt {
    endEvidence(); runtimeEvidence = true;
    for (const lane of lanes) { lane.submittedFrames.clear(); lane.encodedFrames.clear(); }
    const track = source.getVideoTracks()[0], startedAt = performance.now();
    if (!running || failed || pendingLifecycleOperations > 0 || pendingSource || pendingEncoding || lanes.some(l => l.pendingEncoding) ||
        !track || track.readyState === 'ended' || programFrameSourceId(track) !== input.sourceId ||
        typeof input.sourceId !== 'string' || !input.sourceId || typeof input.id !== 'string' || !input.id || input.id.length > 128 || !Number.isFinite(startedAt) ||
        !Number.isFinite(input.expiresAtMonotonicMs) || input.expiresAtMonotonicMs <= startedAt || input.expiresAtMonotonicMs - startedAt > 5000) {
      throw new Error('segmenter: evidence attempt unavailable');
    }
    const { id, sourceId, expiresAtMonotonicMs } = input;
    const attempt = Object.freeze({
      id, sourceId, startedAt,
      isCurrent(): boolean {
        const now = performance.now();
        if (activeAttempt !== attempt) return false;
        if (!running || failed || source.getVideoTracks()[0] !== track || track.readyState === 'ended' ||
            programFrameSourceId(track) !== sourceId || !Number.isFinite(now) || now < startedAt || now >= expiresAtMonotonicMs) {
          endEvidence(); return false;
        }
        return true;
      },
      end(): void { if (activeAttempt === attempt) endEvidence(); },
    });
    activeAttempt = attempt;
    return attempt;
  }

  const runningExtras = (): Lane[] => lanes.filter((l) => !l.main && l.status === 'running');

  function fail(e: unknown): void {
    if (failed) return;
    endEvidence();
    failed = true;
    for (const lane of lanes) clearEncoded(lane);
    sink.onError(toError(e));
  }

  /** A failure of one lane: the whole segmenter for main, only that lane otherwise. */
  function failLane(lane: Lane, e: unknown): void {
    if (lane.main) { fail(e); return; }
    if (lane.status !== 'running') return;
    lane.status = 'failed';
    retireLane(lane);
    try {
      sink.onLaneFailed?.(lane.id, toError(e));
    } catch {
      // A throwing sink must not turn a lane failure into a main failure.
    }
  }

  function retireLane(lane: Lane): void {
    endEvidence();
    clearEncoded(lane);
    const encoder = lane.encoder;
    lane.encoder = null;
    try { if (encoder && encoder.state !== 'closed') encoder.close(); } catch { /* already closed */ }
    lane.muxer = null;
    lane.muxerConfigured = false;
    lane.heldVideo = null;
    lane.queue = [];
  }

  function clearEncoded(lane: Lane): void {
    lane.encodedVideo.length = lane.encodedAudio.length = 0;
    lane.encodedBytes = 0;
  }

  const live = (lane: Lane): boolean => !failed && lane.status === 'running';

  function drainEncoded(lane: Lane, final = false): void {
    while (live(lane) && (final ? lane.encodedVideo.length || lane.encodedAudio.length : lane.encodedVideo.length && lane.encodedAudio.length)) {
      const video = lane.encodedVideo[0], audio = lane.encodedAudio[0];
      const packet = !audio || (video && video.chunk.timestamp <= audio.chunk.timestamp)
        ? lane.encodedVideo.shift()! : lane.encodedAudio.shift()!;
      lane.encodedBytes -= packet.chunk.byteLength;
      if (packet.kind === 'video') processVideoChunk(lane, packet.chunk, packet.meta);
      else processAudioChunk(lane, packet.chunk, packet.meta);
    }
  }

  function orderEncoded(lane: Lane, packet: Queued): void {
    if (!live(lane)) return;
    if (lane.encodedBytes + packet.chunk.byteLength > 16 * 1024 * 1024 || lane.encodedVideo.length + lane.encodedAudio.length >= 512) {
      failLane(lane, new Error('Encoded audio/video queue exceeded byte or packet budget')); return;
    }
    if (packet.kind === 'video') lane.encodedVideo.push(packet); else lane.encodedAudio.push(packet);
    lane.encodedBytes += packet.chunk.byteLength;
    drainEncoded(lane);
    const oldest = Math.min(lane.encodedVideo[0]?.chunk.timestamp ?? Infinity, lane.encodedAudio[0]?.chunk.timestamp ?? Infinity);
    const newest = Math.max(lane.encodedVideo[lane.encodedVideo.length - 1]?.chunk.timestamp ?? -Infinity,
      lane.encodedAudio[lane.encodedAudio.length - 1]?.chunk.timestamp ?? -Infinity);
    if (newest - oldest > 2_000_000) failLane(lane, new Error('Encoded audio/video queue exceeded two seconds'));
  }

  // ---- muxer + splitter ------------------------------------------------------------------------

  /** Extra lanes only: a part that starts where another lane's part started must carry the same seq. */
  function aligned(lane: Lane, startUs: number): boolean {
    const key = Math.round(startUs / 1000);
    const known = partSeqs.get(key);
    if (!known) {
      partSeqs.set(key, { seq: lane.seq, lane });
      while (partSeqs.size > 64) partSeqs.delete(partSeqs.keys().next().value!);
      return true;
    }
    if (known.seq === lane.seq) return true;
    const victim = lane.main ? known.lane : lane;
    failLane(victim, new Error(`segmenter: lane '${victim.id}' lost seq alignment (part at ${key} ms is ${lane.seq} on '${lane.id}', ${known.seq} on '${known.lane.id}')`));
    return victim !== lane;
  }

  function createSplitter(lane: Lane, offsetUs: number): Fmp4Splitter {
    return createFmp4Splitter({
      onInit(bytes) {
        if (lane.codec === 'av1') {
          if (!lane.av1Config) throw new Error('AV1 encoder did not provide a sequence header');
          installAv1Config(bytes, lane.av1Config);
        }
        lane.initTracks = parseInit(bytes);
        lane.videoTrackId = lane.initTracks.find((t) => t.handler === 'vide')?.trackId ?? -1;
        const videoCodec = lane.av1Config?.codec ?? (lane.avcC && avcCodecStringFromAvcC(lane.avcC)) ?? lane.config!.codec;
        const codecs = [videoCodec, ...(audioSel ? [audioSel.config.codec] : [])].join(',');
        const info = { codecs, width: lane.initWidth, height: lane.initHeight };
        if (multiLane) sink.onInit(bytes, info, lane.id);
        else sink.onInit(bytes, info);
      },
      onFragment(bytes) {
        shiftFragmentDecodeTime(bytes, lane.initTracks, offsetUs);
        const video = parseFragment(bytes, lane.initTracks).find((t) => t.trackId === lane.videoTrackId);
        const info = lane.initTracks.find((t) => t.trackId === lane.videoTrackId);
        if (!video || !info) {
          failLane(lane, new Error('segmenter: fragment without a video track'));
          return;
        }
        const independent = isSyncSampleFlags(video.firstSampleFlags);
        if (!independent && opts.segmentMs !== 200) {
          failLane(lane, new Error(`segmenter: segment ${lane.seq} does not start with a sync sample`));
          return;
        }
        const startUs = (video.baseMediaDecodeTime / info.timescale) * 1e6 + (lane.videoZeroUs ?? 0);
        if (multiLane && !aligned(lane, startUs)) return;
        if (independent) lane.gopStartSeq = lane.seq;
        let capturedAt = performance.timeOrigin + clockOriginMs + startUs / 1000;
        // The muxer's timescale rounds timestamps; match the forced keyframe within a millisecond.
        for (const [ts, at] of lane.segmentStarts) {
          if (Math.abs(ts - startUs) <= 1000) capturedAt = at;
          if (ts <= startUs + 1000) lane.segmentStarts.delete(ts);
        }
        const evidence: NonNullable<import('./types').MediaSegment['frameEvidence']> = [];
        // An exact sample payload and integer decode-time match, never wall-clock proximity.
        try {
          const samples = (runtimeEvidence ? activeAttempt?.isCurrent() : opts.captureFrameEvidence) ? videoSamples(bytes, lane.videoTrackId) : [];
          for (const [timestamp, encoded] of lane.encodedFrames) {
            const decodeTime = Math.round((timestamp - lane.videoZeroUs!) * info.timescale / 1e6);
            const sampleIndex = samples.findIndex(sample => sample.decodeTime === decodeTime && sameBytes(sample.bytes, encoded.bytes));
            if (sampleIndex >= 0) {
              const frame = frameEvidence(encoded.track, encoded.timestampUs);
              if (frame && (!encoded.attempt || encoded.attempt.isCurrent() &&
                  frame.sourceId === encoded.attempt.sourceId && Number.isFinite(frame.capturedMonotonicMs) &&
                  frame.capturedMonotonicMs >= encoded.attempt.startedAt && frame.capturedMonotonicMs <= performance.now())) {
                evidence.push(Object.freeze({ sampleIndex, decodeTime, timescale: info.timescale, frame,
                  ...(encoded.attempt ? { attempt: encoded.attempt, encodedSampleSha256: originalSampleDigest(encoded.bytes, encoded.attempt) } : {}) }));
              }
              lane.encodedFrames.delete(timestamp);
            } else if (decodeTime < video.baseMediaDecodeTime) lane.encodedFrames.delete(timestamp);
          }
          evidence.sort((a, b) => a.sampleIndex - b.sampleIndex);
        } catch { /* Evidence failure must never interrupt conventional media. */ }
        if (runtimeEvidence && !activeAttempt?.isCurrent()) evidence.length = 0;
        noteSeqEmitted(lane.seq);
        const seg = {
          ...(evidence.length ? { frameEvidence: evidence } : {}),
          seq: lane.seq++,
          bytes,
          durationMs: (video.duration / info.timescale) * 1000,
          capturedAt,
          keyframe: independent,
          gopStartSeq: lane.gopStartSeq,
        };
        if (multiLane) sink.onSegment(seg, lane.id);
        else sink.onSegment(seg);
      },
    });
  }

  /** A new muxer (and splitter) whose timeline starts `offsetUs` after videoZeroUs. */
  function installMuxer(lane: Lane, offsetUs: number): void {
    const splitter = createSplitter(lane, offsetUs);
    lane.muxerOffsetUs = offsetUs;
    lane.muxerConfigured = false;
    const muxer = muxerFactory();
    lane.muxer = muxer;
    muxer.onData = (bytes) => {
      try {
        splitter.push(bytes);
      } catch (e) {
        failLane(lane, e);
      }
    };
  }

  /**
   * The encoder's SPS/PPS changed (resolution or profile after a reconfigure). They live only in
   * the init segment's avcC, so the current muxer is finalised (emitting its last segment) and a new
   * one takes over at this keyframe; it emits a new init before its first segment.
   */
  function rotateMuxer(lane: Lane, timestampUs: number, size: { width: number; height: number }): void {
    if (lane.muxerConfigured) {
      flushHeldVideo(lane, timestampUs);
      lane.muxer!.finalize();
    }
    lane.heldVideo = null;
    lane.initWidth = size.width;
    lane.initHeight = size.height;
    installMuxer(lane, timestampUs - lane.videoZeroUs!);
    maybeConfigureMuxer(lane);
  }

  function maybeConfigureMuxer(lane: Lane): void {
    if (lane.muxerConfigured || !lane.muxer || lane.videoZeroUs === null) return;
    if (expectAudio && !audioSel) return;
    lane.muxer.configure({
      video: { width: lane.initWidth, height: lane.initHeight, codec: lane.codec === 'av1' ? 'av1' : 'avc' },
      ...(audioSel
        ? { audio: { codec: audioSel.muxCodec, sampleRate: audioSel.config.sampleRate, numberOfChannels: audioSel.config.numberOfChannels } }
        : {}),
      minFragmentSeconds: (opts.segmentMs / 1000) * MIN_FRAGMENT_FRACTION,
    });
    lane.muxerConfigured = true;
    // Video first: the muxer's cross-track offset must see the video track's t=0 before any audio.
    const pending = lane.queue;
    lane.queue = [];
    for (const q of pending) if (q.kind === 'video') muxVideo(lane, q.chunk, q.meta);
    for (const q of pending) if (q.kind === 'audio') muxAudio(lane, q.chunk, q.meta);
  }

  function muxVideo(lane: Lane, chunk: EncodedVideoChunk, meta?: EncodedVideoChunkMetadata): void {
    flushHeldVideo(lane, chunk.timestamp);
    lane.heldVideo = { chunk, meta };
  }

  /** Muxes the held chunk; it lasts until `untilUs` (the next chunk), or one frame when unknown. */
  function flushHeldVideo(lane: Lane, untilUs?: number): void {
    const held = lane.heldVideo;
    if (!held) return;
    lane.heldVideo = null;
    const ts = held.chunk.timestamp;
    const durationUs = untilUs !== undefined && untilUs > ts ? untilUs - ts : Math.round(1e6 / lane.fps);
    lane.muxer!.addVideo(held.chunk, held.meta, ts - lane.videoZeroUs! - lane.muxerOffsetUs, lane.segmentStarts.has(ts), durationUs);
  }

  function muxAudio(lane: Lane, chunk: EncodedAudioChunk, meta?: EncodedAudioChunkMetadata): void {
    const t = chunk.timestamp - lane.videoZeroUs! - lane.muxerOffsetUs;
    if (t < 0) return; // before the first video frame of this muxer
    lane.muxer!.addAudio(chunk, meta, t);
  }

  function onVideoChunk(lane: Lane, chunk: EncodedVideoChunk, meta: EncodedVideoChunkMetadata | undefined, encoderGeneration: number): void {
    // A closed/replaced encoder cannot mutate the new generation, including its ordinary muxer.
    if (encoderGeneration !== lane.encoderGeneration) return;
    lane.lastOutputAt = performance.now();
    const submitted = lane.submittedFrames.get(chunk.timestamp);
    lane.submittedFrames.delete(chunk.timestamp);
    if (submitted && submitted.encoderGeneration === encoderGeneration && (!submitted.attempt || submitted.attempt.isCurrent())) {
      const retained = [...lane.encodedFrames.values()].reduce((n, entry) => n + entry.bytes.length, 0);
      if (runtimeEvidence && (lane.encodedFrames.size >= 180 || retained + chunk.byteLength > 8 * 1024 * 1024)) endEvidence();
      else {
        const bytes = new Uint8Array(chunk.byteLength); chunk.copyTo(bytes);
        lane.encodedFrames.set(chunk.timestamp, { ...submitted, bytes });
        let retainedBytes = [...lane.encodedFrames.values()].reduce((n, entry) => n + entry.bytes.length, 0);
        while (lane.encodedFrames.size > 180 || retainedBytes > 8 * 1024 * 1024) {
          const first = lane.encodedFrames.keys().next().value!; retainedBytes -= lane.encodedFrames.get(first)!.bytes.length; lane.encodedFrames.delete(first);
        }
      }
    }
    if (!lane.main && lane.status !== 'running') return;
    if (expectAudio) orderEncoded(lane, {kind:'video',chunk,meta});
    else processVideoChunk(lane, chunk, meta);
  }

  function processVideoChunk(lane: Lane, chunk: EncodedVideoChunk, meta?: EncodedVideoChunkMetadata): void {
    try {
      if (lane.videoZeroUs === null) {
        if (!lane.segmentStarts.has(chunk.timestamp)) return; // never start mid-GOP
        lane.videoZeroUs = chunk.timestamp;
      }
      const description = lane.codec === 'av1' ? undefined : meta?.decoderConfig?.description;
      const coded =
        meta?.decoderConfig?.codedWidth && meta.decoderConfig.codedHeight
          ? { width: meta.decoderConfig.codedWidth, height: meta.decoderConfig.codedHeight }
          : { width: lane.encWidth, height: lane.encHeight };
      if (lane.codec === 'av1' && chunk.type === 'key') {
        const bytes = new Uint8Array(chunk.byteLength); chunk.copyTo(bytes);
        const next = av1SequenceConfig(bytes);
        if (!next) throw new Error('AV1 keyframe is missing its sequence header');
        if (!lane.av1Config) {
          lane.av1Config = next; lane.initWidth = next.width; lane.initHeight = next.height;
        } else if (!sameBytes(lane.av1Config.sequence, next.sequence)) {
          endEvidence();
          lane.segmentStarts.set(chunk.timestamp, performance.timeOrigin + clockOriginMs + chunk.timestamp / 1000);
          // Finalize the previous init before replacing its configuration.
          flushHeldVideo(lane, chunk.timestamp);
          lane.muxer!.finalize();
          lane.av1Config = next; lane.initWidth = next.width; lane.initHeight = next.height;
          installMuxer(lane, chunk.timestamp - lane.videoZeroUs!); maybeConfigureMuxer(lane);
        }
      }
      if (description) {
        const next = copyDescription(description);
        if (!lane.avcC) {
          lane.avcC = next;
          lane.initWidth = coded.width;
          lane.initHeight = coded.height;
        } else if (!sameBytes(lane.avcC, next)) {
          endEvidence();
          // Encoders repeat an unchanged avcC after a bitrate-only reconfigure: only a real change
          // (new SPS/PPS) needs a new init. It normally arrives on one of our forced keyframes, but
          // an encoder may also re-initialise on its own (Chromium does when the source size
          // changes) and send the new SPS on a keyframe of its choice: that keyframe then starts a
          // segment early. New parameters on a delta frame cannot be cut cleanly.
          if (!lane.segmentStarts.has(chunk.timestamp)) {
            if (chunk.type !== 'key') {
              throw new Error('segmenter: the encoder changed its configuration on a non-key frame');
            }
            lane.segmentStarts.set(chunk.timestamp, performance.timeOrigin + clockOriginMs + chunk.timestamp / 1000);
          }
          lane.avcC = next;
          rotateMuxer(lane, chunk.timestamp, coded);
        }
      }
      if (!lane.muxerConfigured) {
        lane.queue.push({ kind: 'video', chunk, meta });
        maybeConfigureMuxer(lane);
        return;
      }
      muxVideo(lane, chunk, meta);
    } catch (e) {
      failLane(lane, e);
    }
  }

  function onAudioChunk(chunk: EncodedAudioChunk, meta?: EncodedAudioChunkMetadata): void {
    // One AAC encode, muxed identically into every lane.
    for (const lane of lanes) if (lane.main || lane.status === 'running') orderEncoded(lane, {kind:'audio',chunk,meta});
  }

  function processAudioChunk(lane: Lane, chunk: EncodedAudioChunk, meta?: EncodedAudioChunkMetadata): void {
    try {
      if (!lane.muxerConfigured) {
        lane.queue.push({ kind: 'audio', chunk, meta });
        return;
      }
      muxAudio(lane, chunk, meta);
    } catch (e) {
      failLane(lane, e);
    }
  }

  // ---- sources ---------------------------------------------------------------------------------

  function attach(src: MediaStream): void {
    const gen = generation;
    const [videoTrack] = src.getVideoTracks();
    const [audioTrack] = src.getAudioTracks();
    if (!videoTrack) throw new Error('segmenter: stream has no video track');
    const v = new MediaStreamTrackProcessor({ track: videoTrack }).readable.getReader();
    readers.add(v);
    ownSourceWork(pumpVideo(v, gen, videoTrack), true);
    if (audioTrack && expectAudio) {
      const a = new MediaStreamTrackProcessor({ track: audioTrack }).readable.getReader();
      readers.add(a);
      ownSourceWork(pumpAudio(a, gen), true);
    }
  }

  function detach(): void {
    generation++;
    for (const r of readers) {
      const cancellation = Promise.resolve().then(() => r.cancel()).finally(() => { r.releaseLock(); });
      ownSourceWork(cancellation, false);
    }
    readers.clear();
  }

  function swapSource(): void {
    endEvidence();
    const next = pendingSource!;
    pendingSource = null;
    source = next;
    for (const lane of lanes) { lane.submittedFrames.clear(); lane.encodedFrames.clear(); }
    detach();
    requestBoundary(boundary);
    attach(next);
  }

  /** Whether an extra lane encodes this frame: boundaries always, else on its cadence and while its queue has room. */
  function extraTakes(lane: Lane, ts: number, isBoundary: boolean): boolean {
    if (isBoundary) {
      lane.nextDueUs = ts + lane.frameIntervalUs;
      return true;
    }
    if (lane.frameIntervalUs > 0 && lane.nextDueUs !== null) {
      // A quarter interval of slack absorbs capture jitter; the due time advances on the grid, so the rate holds.
      if (ts < lane.nextDueUs - lane.frameIntervalUs / 4) return false;
      lane.nextDueUs += lane.frameIntervalUs;
      if (lane.nextDueUs <= ts) lane.nextDueUs = ts + lane.frameIntervalUs;
    }
    if (lane.encoder!.encodeQueueSize > maxVideoQueue) {
      endEvidence();
      lane.droppedFrames++;
      return false;
    }
    return true;
  }

  async function pumpVideo(reader: ReadableStreamDefaultReader<VideoFrame>, gen: number, track: MediaStreamTrack): Promise<void> {
    const rebase = createRebaser(clockOriginMs);
    for (;;) {
      const { value: frame, done } = await reader.read();
      if (done) {
        if (gen === generation && pendingSource) swapSource();
        return;
      }
      if (gen !== generation || !running || failed) {
        frame.close();
        return;
      }
      const ts = rebase(frame.timestamp);
      if (ts <= lastVideoTs) {
        endEvidence();
        frame.close();
        continue;
      }
      if (pendingSource && boundary.nextBoundaryUs !== null && ts >= boundary.nextBoundaryUs) {
        frame.close();
        swapSource();
        return;
      }
      if (mainLane.encoder!.encodeQueueSize > STALL_QUEUE && performance.now() - mainLane.lastOutputAt > STALL_MS) {
        replaceStuckEncoder();
      }
      const extras = runningExtras();
      for (const lane of extras) {
        if (lane.encoder!.encodeQueueSize > STALL_QUEUE && performance.now() - lane.lastOutputAt > STALL_MS) {
          failLane(lane, new Error(`segmenter: the ${lane.id} video encoder stopped producing output (${lane.encoder!.encodeQueueSize} frames waiting)`));
        }
      }
      const isBoundary = decideBoundary(boundary, ts);
      const encoder = mainLane.encoder!;
      const mainTakes = isBoundary || encoder.encodeQueueSize <= maxVideoQueue;
      if (!mainTakes) { endEvidence(); mainLane.droppedFrames++; }
      const takers = extras.filter((lane) => lane.status === 'running' && extraTakes(lane, ts, isBoundary));
      if (!mainTakes && takers.length === 0) {
        frame.close();
        continue;
      }
      if (isBoundary) {
        if (pendingEncoding) applyEncoding();
        for (const lane of takers) if (lane.pendingEncoding) applyLaneEncoding(lane);
        const at = performance.timeOrigin + clockOriginMs + ts / 1000;
        mainLane.segmentStarts.set(ts, at);
        for (const lane of takers) lane.segmentStarts.set(ts, at);
      }
      lastVideoTs = ts;
      const originalTimestamp = frame.timestamp;
      const remember = (lane: Lane) => {
        const attempt = activeAttempt;
        if (runtimeEvidence ? !attempt?.isCurrent() : !opts.captureFrameEvidence) return;
        lane.submittedFrames.set(ts, { track, timestampUs: originalTimestamp, encoderGeneration: lane.encoderGeneration,
          ...(attempt ? { attempt } : {}) });
        while (lane.submittedFrames.size > 180) {
          if (runtimeEvidence) { endEvidence(); break; }
          lane.submittedFrames.delete(lane.submittedFrames.keys().next().value!);
        }
      };
      const restamped = new VideoFrame(frame, { timestamp: ts });
      frame.close();
      try {
        if (mainTakes) { remember(mainLane); encoder.encode(restamped, { keyFrame: isBoundary }); }
        for (const lane of takers) {
          if (lane.status !== 'running') continue;
          try {
            remember(lane); lane.encoder!.encode(restamped, { keyFrame: isBoundary });
          } catch (e) {
            failLane(lane, e);
          }
        }
      } finally {
        restamped.close();
      }
    }
  }

  function newVideoEncoder(lane: Lane, config: VideoEncoderConfig): VideoEncoder {
    endEvidence();
    const encoderGeneration = ++lane.encoderGeneration;
    const encoder = new VideoEncoder({ output: (chunk, meta) => onVideoChunk(lane, chunk, meta, encoderGeneration), error: (e) => { if (encoderGeneration === lane.encoderGeneration) failLane(lane, e); } });
    encoder.configure(config);
    lane.lastOutputAt = performance.now();
    return encoder;
  }

  /**
   * The main encoder holds frames and emits nothing: close it and continue in software, once. The next
   * frame starts a segment; a different avcC from the new encoder brings a new init (rotateMuxer).
   */
  function replaceStuckEncoder(): void {
    const stuck = mainLane.encoder!;
    const queued = stuck.encodeQueueSize;
    if (mainLane.config!.hardwareAcceleration === 'prefer-software') {
      throw new Error(`segmenter: the video encoder stopped producing output (${queued} frames waiting)`);
    }
    try {
      stuck.close();
    } catch {
      // already closed
    }
    mainLane.submittedFrames.clear(); mainLane.encodedFrames.clear();
    mainLane.config = { ...mainLane.config!, hardwareAcceleration: 'prefer-software' };
    mainLane.encoder = newVideoEncoder(mainLane, mainLane.config);
    mainLane.encoderFallback = `prefer-software (the ${opts.hardwareAcceleration ?? 'no-preference'} encoder stalled with ${queued} frames waiting)`;
    requestBoundary(boundary);
  }

  /** Applies a video bitrate/size update to a lane's encoder. */
  function reconfigureVideo(lane: Lane, p: EncodingUpdate): void {
    endEvidence();
    if (!lane.config || (p.videoKbps === undefined && p.width === undefined && p.height === undefined)) return;
    const width = p.width ?? lane.encWidth;
    const height = p.height ?? lane.encHeight;
    const resized = width !== lane.encWidth || height !== lane.encHeight;
    lane.config = {
      ...lane.config,
      ...(p.videoKbps !== undefined ? { bitrate: p.videoKbps * 1000 } : {}),
      ...(resized
        ? { width, height, codec: lane.codec === 'av1' ? lane.config.codec : `${lane.config.codec.slice(0, 9)}${h264LevelHex(width, height, lane.fps)}` }
        : {}),
    };
    lane.encWidth = width;
    lane.encHeight = height;
    lane.encoder!.configure(lane.config);
  }

  function applyEncoding(): void {
    const p = pendingEncoding!;
    pendingEncoding = null;
    reconfigureVideo(mainLane, p);
    if (p.audioKbps !== undefined && audioSel) {
      audioSel = { ...audioSel, config: { ...audioSel.config, bitrate: p.audioKbps * 1000 } };
      audioReconfigure = true;
    }
  }

  function applyLaneEncoding(lane: Lane): void {
    const p = lane.pendingEncoding!;
    lane.pendingEncoding = null;
    try {
      reconfigureVideo(lane, p);
    } catch (e) {
      failLane(lane, e);
    }
  }

  async function configureAudio(sampleRate: number, numberOfChannels: number): Promise<void> {
    const sel = await selectAudioCodec({ sampleRate, numberOfChannels, audioKbps: opts.audioKbps }, (c) =>
      (opts.segmentMs === 200 || opts.codec === 'av1') && c.codec !== 'mp4a.40.2' ? Promise.resolve({ supported: false }) : AudioEncoder.isConfigSupported(c));
    if (!sel) throw new Error(`segmenter: no supported audio encoder (AAC or Opus) for ${sampleRate} Hz x${numberOfChannels}`);
    audioSel = sel;
    audioEncoder = new AudioEncoder({ output: onAudioChunk, error: fail });
    audioEncoder.configure(sel.config);
    for (const lane of lanes) if (lane.main || lane.status === 'running') maybeConfigureMuxer(lane);
  }

  async function pumpAudio(reader: ReadableStreamDefaultReader<AudioData>, gen: number): Promise<void> {
    const rebase = createRebaser(clockOriginMs);
    // The audio device's clock is not the wall clock the video is stamped from; keep the two together.
    let drift: ReturnType<typeof createDriftCorrector> | null = null;
    for (;;) {
      const { value: data, done } = await reader.read();
      if (done) return;
      if (gen !== generation || !running || failed) {
        data.close();
        return;
      }
      try {
        if (!audioSel) {
          audioConfiguring ??= configureAudio(data.sampleRate, data.numberOfChannels);
          await audioConfiguring;
        } else if (data.sampleRate !== audioSel.config.sampleRate || data.numberOfChannels !== audioSel.config.numberOfChannels) {
          throw new Error('segmenter: replacement audio track changed sample rate or channel count');
        }
        if (audioReconfigure) {
          audioReconfigure = false;
          audioEncoder!.configure(audioSel!.config);
        }
        const natural = rebase(data.timestamp);
        drift ??= createDriftCorrector(data.sampleRate);
        const stretchable = data.format === 'f32-planar';
        const plan = drift.plan(data.numberOfFrames, natural, (performance.now() - clockOriginMs) * 1000, stretchable);
        const ts = plan.tsUs;
        if (ts <= lastAudioTs) continue;
        lastAudioTs = ts;
        const restamped =
          plan.outFrames === data.numberOfFrames ? copyAudioData(data, ts) : stretchAudioData(data, ts, plan.outFrames);
        try {
          audioEncoder!.encode(restamped);
        } finally {
          restamped.close();
        }
      } finally {
        data.close();
      }
    }
  }

  // ---- public API ------------------------------------------------------------------------------

  function resetLane(lane: Lane, seqBase: number): void {
    lane.seq = seqBase;
    lane.droppedFrames = 0;
    lane.gopStartSeq = seqBase;
    lane.videoZeroUs = null;
    lane.segmentStarts.clear();
    lane.submittedFrames.clear(); lane.encodedFrames.clear();
    lane.avcC = null;
    lane.av1Config = null;
    lane.heldVideo = null;
    lane.initWidth = lane.encWidth;
    lane.initHeight = lane.encHeight;
    lane.queue = [];
    lane.nextDueUs = null;
    clearEncoded(lane);
    installMuxer(lane, 0);
  }

  /** Extra lanes' encoders. A lane that cannot start fails alone; main goes on. */
  async function startExtraLanes(): Promise<void> {
    for (const lane of runningExtras()) {
      try {
        const sel = lane.presetConfig ? { config: lane.presetConfig } : await selectVideoCodec(
          { codec: lane.codec, width: lane.encWidth, height: lane.encHeight, fps: lane.fps, videoKbps: lane.videoKbps,
            hardwareAcceleration: lane.hardwareAcceleration ?? 'no-preference' },
          (c) => VideoEncoder.isConfigSupported(c),
        );
        if (!sel) throw new Error(`segmenter: no supported ${lane.codec === 'av1' ? 'AV1' : 'H.264'} encoder for lane '${lane.id}'`);
        lane.config = sel.config;
        lane.encoder = newVideoEncoder(lane, lane.config);
      } catch (e) {
        failLane(lane, e);
      }
    }
  }

  async function doStart(): Promise<void> {
    if (running) return;
    // Taken before the async codec probe so it reflects when start() was called.
    const runSeqBase = opts.seqBase ?? nextDefaultSeqBase(Date.now(), opts.segmentMs === 200 ? 200 : 1000);
    if (typeof MediaStreamTrackProcessor !== 'function' || typeof VideoEncoder !== 'function') {
      throw new Error('segmenter: WebCodecs and MediaStreamTrackProcessor are required');
    }
    const sel = opts.videoConfig ? { config: opts.videoConfig } : await selectVideoCodec(
      {
        codec: opts.codec,
        width: mainLane.encWidth,
        height: mainLane.encHeight,
        fps: opts.fps,
        videoKbps: opts.videoKbps,
        hardwareAcceleration: opts.hardwareAcceleration ?? 'no-preference',
      },
      (c) => VideoEncoder.isConfigSupported(c),
    );
    if (!sel) throw new Error(`segmenter: no supported ${opts.codec === 'av1' ? 'AV1' : 'H.264'} encoder`);
    mainLane.config = sel.config;
    mainLane.encoder = newVideoEncoder(mainLane, mainLane.config);
    mainLane.encoderFallback = null;

    failed = false;
    // Per run, not per source: replaceTracks keeps counting from here. Every lane counts from the same base.
    boundary = createBoundaryState(gopMs);
    lastVideoTs = lastAudioTs = -Infinity;
    partSeqs.clear();
    for (const lane of lanes) if (lane.main || lane.status === 'running') resetLane(lane, runSeqBase);
    audioSel = null;
    audioConfiguring = null;
    if (multiLane) await startExtraLanes();
    expectAudio = source.getAudioTracks().length > 0;
    clockOriginMs = performance.now();
    running = true;
    attach(source);
  }

  async function doStop(): Promise<void> {
    if (!running) return;
    running = false;
    detach();
    // A replacement that had not reached its boundary yet becomes the source of the next start().
    if (pendingSource) {
      source = pendingSource;
      pendingSource = null;
    }
    const video = mainLane.encoder;
    const audio = audioEncoder;
    const extras = runningExtras().map((lane) => ({ lane, encoder: lane.encoder }));
    mainLane.encoder = null;
    for (const { lane } of extras) lane.encoder = null;
    audioEncoder = null;
    try {
      if (video && video.state === 'configured') await video.flush();
      if (audio && audio.state === 'configured') await audio.flush();
      drainEncoded(mainLane, true);
    } catch (e) {
      if (!failed) fail(e);
    }
    if (mainLane.muxerConfigured) {
      try {
        flushHeldVideo(mainLane);
        mainLane.muxer?.finalize();
      } catch (e) {
        fail(e);
      }
    }
    // Extra lanes after main is out: a slow lane flush never holds up main's last part.
    for (const { lane, encoder } of extras) {
      if (lane.status !== 'running') continue;
      let timer: ReturnType<typeof setTimeout> | undefined;
      try {
        const flushing = encoder && encoder.state === 'configured' ? encoder.flush().then(() => true) : null;
        // A flush that loses the race may still reject after the lane is retired: never an unhandled rejection.
        flushing?.catch(() => false);
        const flushed = flushing
          ? await Promise.race([flushing, new Promise<false>((resolve) => { timer = setTimeout(() => resolve(false), EXTRA_FLUSH_MS); })])
          : true;
        if (!flushed) { lane.status = 'dropped'; retireLane(lane); continue; }
        drainEncoded(lane, true);
      } catch (e) {
        failLane(lane, e);
      } finally {
        clearTimeout(timer);
      }
    }
    for (const { lane } of extras) {
      if (lane.status !== 'running' || !lane.muxerConfigured) continue;
      try {
        flushHeldVideo(lane);
        lane.muxer?.finalize();
      } catch (e) {
        failLane(lane, e);
      }
    }
    for (const enc of [video, audio, ...extras.map((x) => x.encoder)]) {
      if (enc && enc.state !== 'closed') enc.close();
    }
    for (const lane of lanes) {
      lane.muxer = null;
      lane.muxerConfigured = false;
    }
  }

  /**
   * Queues a lifecycle operation behind the previous one. Repeating the latest operation returns its
   * promise (a second start() while starting or running is a no-op; likewise stop()), so the state
   * always ends up matching the last call and encoders are created and closed exactly once each.
   */
  function enqueue(kind: 'start' | 'stop', op: () => Promise<void>): Promise<void> {
    if (lastOp?.kind === kind) return lastOp.done;
    if (lastOp === null && kind === 'stop') return Promise.resolve();
    pendingLifecycleOperations++;
    const done = lifecycle.catch(() => undefined).then(op).finally(() => { pendingLifecycleOperations--; });
    const entry = { kind, done };
    lastOp = entry;
    lifecycle = done;
    // A failed start must not make later start() calls return the same rejection.
    done.catch(() => {
      if (lastOp === entry) lastOp = null;
    });
    return done;
  }

  function updateEncoding(p: EncodingUpdate): void {
    validEncodingUpdate(p);
    endEvidence();
    pendingEncoding = { ...pendingEncoding, ...p };
  }

  const videoCodecOf = (lane: Lane): string | null =>
    lane.av1Config?.codec ?? (lane.avcC ? avcCodecStringFromAvcC(lane.avcC) : (lane.config?.codec ?? null));

  return {
    waitForSourceCleanup,
    beginFrameEvidence,
    start: () => enqueue('start', doStart),
    stop: () => { endEvidence(); return enqueue('stop', doStop); },

    replaceTracks(next) {
      endEvidence();
      if (!running) {
        source = next;
        return;
      }
      pendingSource = next;
      // An ended track cannot supply the frame that normally triggers the handoff.
      if (source.getVideoTracks()[0]?.readyState === 'ended') swapSource();
    },

    updateEncoding,

    updateLaneEncoding(laneId, p) {
      if (laneId === 'main') { updateEncoding(p); return; }
      validEncodingUpdate(p);
      endEvidence();
      const lane = lanes.find((l) => l.id === laneId);
      if (!lane || lane.status !== 'running') return;
      lane.pendingEncoding = { ...lane.pendingEncoding, videoKbps: p.videoKbps, width: p.width, height: p.height };
      for (const k of ['videoKbps', 'width', 'height'] as const) if (lane.pendingEncoding[k] === undefined) delete lane.pendingEncoding[k];
    },

    dropLane(laneId) {
      const lane = lanes.find((l) => l.id === laneId);
      if (!lane || lane.main || lane.status !== 'running') return;
      lane.status = 'dropped';
      retireLane(lane);
    },

    getState(): SegmenterState {
      return {
        running,
        seq: mainLane.seq,
        droppedFrames: mainLane.droppedFrames,
        encoderQueue: mainLane.encoder?.encodeQueueSize ?? 0,
        videoCodec: videoCodecOf(mainLane),
        audioCodec: audioSel?.config.codec ?? null,
        ...(mainLane.config ? { implementation: mainLane.encoderFallback ?? mainLane.config.hardwareAcceleration ?? 'no-preference' } : {}),
        ...(multiLane ? {
          lanes: Object.fromEntries(lanes.filter((l) => !l.main).map((l) => [l.id, {
            status: l.status,
            droppedFrames: l.droppedFrames,
            encoderQueue: l.encoder?.encodeQueueSize ?? 0,
            videoCodec: videoCodecOf(l),
            ...(l.config ? { implementation: l.config.hardwareAcceleration ?? 'no-preference' } : {}),
          }])),
        } : {}),
      };
    },
  };
}
